import React, { useEffect, useState } from 'react';
import './Alerts.css';
import { api, endpoints } from '../config/api';
import { Spinner, Button } from '../components/ui';
import { useT } from '../i18n';

const messages = {
  docTitle: { tr: 'Uyarılar - Signature Manager', en: 'Alerts - Signature Manager' },
  signatureSystem: { tr: 'İmza Sistemi', en: 'Signature System' },
  title: { tr: 'Uyarılar', en: 'Alerts' },
  subtitle: { tr: 'Sistem bildirimleri ve imza sistemi uyarıları', en: 'System notices and signature system alerts' },
  emptyTitle: { tr: 'Henüz uyarı yok', en: 'No alerts yet' },
  emptyText: { tr: 'Sistem uyarıları ve bildirimler burada görünecektir.', en: 'System alerts and notices will appear here.' },
  system: { tr: 'Sistem', en: 'System' },
  markRead: { tr: 'Okundu İşaretle', en: 'Mark as Read' },
};

const ALERT_ICON_PATHS = {
  success: <path d="M5 13l4 4L19 7" />,
  warning: <><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></>,
  info: <><path d="M12 16v-4M12 8h.01" /><circle cx="12" cy="12" r="9" /></>,
  error: <path d="M18 6 6 18M6 6l12 12" />,
};

function AlertTypeIcon({ type }) {
  const paths = ALERT_ICON_PATHS[type] || ALERT_ICON_PATHS.info;
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {paths}
    </svg>
  );
}

function Alerts() {
  const [signatureAlerts, setSignatureAlerts] = useState([]);
  const [systemNotifications, setSystemNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t, formatDateTime } = useT(messages);

  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);

  useEffect(() => {

    const fetchAllAlerts = async () => {
      setLoading(true);
      try {
        // Fetch both types of alerts
        const [sigAlerts, sysNotifs] = await Promise.all([
          api.get(endpoints.signatures.alerts).catch(() => []),
          api.get(endpoints.admin.notifications.user).catch(() => ({ unread: [], read: [] }))
        ]);

        setSignatureAlerts(Array.isArray(sigAlerts) ? sigAlerts : []);

        // Combine unread and read system notifications
        const allSysNotifs = [
          ...(sysNotifs.unread || []),
          ...(sysNotifs.read || [])
        ];
        setSystemNotifications(allSysNotifs);
      } catch (err) {
        console.error('Error fetching alerts:', err);
        setSignatureAlerts([]);
        setSystemNotifications([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllAlerts();
    // Refresh every 30 seconds
    const interval = setInterval(fetchAllAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await api.post(endpoints.admin.notifications.markRead(notificationId));
      // Remove from list or update status
      setSystemNotifications(prev =>
        prev.filter(n => n.id !== notificationId)
      );
    } catch (err) {
      console.error('Error marking notification as read:', err);
    }
  };

  const markSignatureAlertRead = async (alertId) => {
    try {
      await api.patch(endpoints.signatures.alertDetail(alertId));
      setSignatureAlerts(prev => prev.map(a => a.id === alertId ? { ...a, is_read: true } : a));
    } catch (err) {
      console.error('Error marking alert as read:', err);
    }
  };

  const deleteSignatureAlert = async (alertId) => {
    try {
      await api.delete(endpoints.signatures.alertDetail(alertId));
      setSignatureAlerts(prev => prev.filter(a => a.id !== alertId));
    } catch (err) {
      console.error('Error deleting alert:', err);
    }
  };

  // Combine all alerts for unified display
  const allAlerts = [
    ...systemNotifications.map(notif => ({
      id: `sys-${notif.id}`,
      timestamp: notif.created_at,
      type: notif.notification_type,
      title: notif.title,
      message: notif.message,
      isSystem: true,
      notificationId: notif.id
    })),
    ...signatureAlerts.map(alert => ({
      id: `sig-${alert.id}`,
      alertId: alert.id,
      timestamp: alert.timestamp,
      type: alert.type,
      title: t('signatureSystem'),
      message: alert.message,
      isSystem: false,
      isRead: alert.is_read,
    }))
  ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));

  return (
    <div className="page-stack">
      <h1>{t('title')}</h1>
      <p className="alerts-subtitle">{t('subtitle')}</p>

      {loading ? (
        <div className="page-loading"><Spinner /></div>
      ) : allAlerts.length === 0 ? (
        <div className="empty-alerts-message">
          <div className="empty-icon">🔔</div>
          <h3>{t('emptyTitle')}</h3>
          <p>{t('emptyText')}</p>
        </div>
      ) : (
        <div className="alerts-list">
          {allAlerts.map((alert) => {
            const type = ALERT_ICON_PATHS[alert.type] ? alert.type : 'info';
            return (
              <div className={`alert-card alert-card-${type}${!alert.isSystem && alert.isRead ? ' alert-card-read' : ''}`} key={alert.id}>
                <span className="alert-card-icon"><AlertTypeIcon type={type} /></span>
                <div className="alert-card-body">
                  <div className="alert-card-title-row">
                    <span className="alert-card-title">{alert.isSystem ? alert.title : t('signatureSystem')}</span>
                    {alert.isSystem && <span className="alert-system-badge">{t('system')}</span>}
                  </div>
                  <p className="alert-card-message">{alert.message}</p>
                </div>
                <div className="alert-card-time">
                  {formatDateTime(alert.timestamp, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
                {alert.isSystem && (
                  <button
                    className="alert-close-btn"
                    onClick={() => markAsRead(alert.notificationId)}
                    title={t('close')}
                  >
                    ×
                  </button>
                )}
                {!alert.isSystem && (
                  <div className="alert-card-actions">
                    {!alert.isRead && (
                      <Button variant="secondary" onClick={() => markSignatureAlertRead(alert.alertId)}>
                        {t('markRead')}
                      </Button>
                    )}
                    <Button variant="danger" onClick={() => deleteSignatureAlert(alert.alertId)}>
                      {t('delete')}
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Alerts;
